import React, { useEffect, useState } from "react";
import {
  FaUsers,
  FaDollarSign,
  FaMapMarkerAlt,
  FaChartLine,
  FaArrowUp,
  FaArrowDown,
  FaBell,
  FaSearch,
  FaCalendarAlt,
  FaClock,
  FaEye,
  FaPlus,
  FaFilter,
  FaDownload,
  // FaRefresh,
  FaExclamationTriangle,
  FaCheckCircle,
  FaClock as FaPending,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { UserData } from "../Functions/firebaseFunction";
import Sidebar from "../components/SideBar";
import useStore from "../store/useStore";
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { Pie, Bar } from "react-chartjs-2";
import { listenToLocationsCount } from "./LocationSection/locationsfunction";
import getTransactions from "../Functions/transactionsFunct";
import {
  ShimmerCard,
  ShimmerChart,
  ShimmerTransactionCard,
} from "../components/ShimmerComponents";
import DashboardCharts from "./DashboardSection/DashboardGraphs";
import DashboardStats from "./DashboardSection/DashboardStats";
import DashboardTransactions from "./DashboardSection/DashboardTransactions";
import OnboardingTour from "../components/OnboardingTour";

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);

const LoadingSpinner = ({ size = "w-4 h-4" }) => (
  <div
    className={`${size} animate-spin rounded-full border-2 border-gray-300 border-t-blue-600`}
  ></div>
);

const StatCard = ({ icon: Icon, title, value, loading, subtitle }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300 p-6 group">
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-center space-x-4">
        <div
          className={`w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center text-white shadow-sm`}
        >
          <Icon className="text-xl" />
        </div>
        <div>
          <h3 className="text-sm font-medium text-gray-600 mb-1">{title}</h3>
          <p className="text-2xl font-bold text-gray-900">
            {loading ? (
              <div className="animate-pulse bg-gray-200 h-8 w-20 rounded"></div>
            ) : (
              value
            )}
          </p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
      </div>
    </div>
  </div>
);

const ChartCard = ({
  title,
  children,
  className = "",
  actionButton,
  loading = false,
}) => (
  <div
    className={`bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300 p-6 ${className}`}
  >
    <div className="flex items-center justify-between mb-6">
      <h3 className="text-xl font-semibold text-gray-900 flex items-center">
        <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-purple-600 rounded-full mr-3"></div>
        {title}
      </h3>
      {actionButton && actionButton}
    </div>
    <div className="h-64">
      {loading ? (
        <div className="h-full bg-gray-100 rounded-lg animate-pulse flex items-center justify-center">
          <div className="text-gray-400 text-sm">Loading chart...</div>
        </div>
      ) : (
        children
      )}
    </div>
  </div>
);

const TransactionRow = ({ transaction, index }) => {
  const formatDate = (timestamp) => {
    let date;

    // Handle Firebase Timestamp objects
    if (timestamp && typeof timestamp.toDate === "function") {
      date = timestamp.toDate();
    } else if (timestamp && timestamp.seconds) {
      // Handle timestamp with seconds property
      date = new Date(
        timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000
      );
    } else {
      date = new Date(timestamp);
    }

    return {
      date: date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
      time: date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "confirmed":
        return <FaCheckCircle className="text-green-500" />;
      case "pending":
        return <FaPending className="text-yellow-500" />;
      case "cancelled":
        return <FaExclamationTriangle className="text-red-500" />;
      default:
        return <FaClock className="text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      confirmed: "bg-green-100 text-green-700",
      pending: "bg-yellow-100 text-yellow-700",
      cancelled: "bg-red-100 text-red-700",
    };
    return `px-3 py-1 rounded-full text-xs font-medium ${
      badges[status] || badges.pending
    }`;
  };

  const { date, time } = formatDate(transaction.createdAt);
  const serviceName =
    transaction.services && transaction.services[0]
      ? transaction.services[0].serviceName
      : "Service";
  const customerName = transaction.scannedUser
    ? `${transaction.scannedUser.firstName} ${transaction.scannedUser.lastName}`
    : "Customer";

  return (
    <div
      className={`grid grid-cols-4 gap-4 py-4 px-4 hover:bg-gray-50 rounded-lg transition-colors ${
        index > 0 ? "border-t border-gray-100" : ""
      }`}
    >
      <div className="flex items-center space-x-3">
        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
        <div>
          <span className="text-gray-900 font-medium block">{date}</span>
          <span className="text-xs text-gray-500">{time}</span>
        </div>
      </div>
      <div>
        <span className="text-gray-700 font-medium block">{serviceName}</span>
        <span className="text-xs text-gray-500">{customerName}</span>
      </div>
      <div className="flex items-center space-x-2">
        {getStatusIcon(transaction.status)}
        <span className={getStatusBadge(transaction.status)}>
          {transaction.status}
        </span>
      </div>
      <span className="text-gray-900 font-semibold">
        ${transaction.totalAmount}
      </span>
    </div>
  );
};

const QuickActionCard = ({
  icon: Icon,
  title,
  subtitle,
  onClick,
  color = "blue",
}) => (
  <button
    onClick={onClick}
    className="bg-white hover:bg-gray-50 border border-gray-200 rounded-xl p-4 transition-all duration-300 group text-left"
  >
    <div className="flex items-center space-x-3">
      <div
        className={`w-10 h-10 bg-gradient-to-br from-${color}-500 to-${color}-600 rounded-lg flex items-center justify-center text-white group-hover:scale-110 transition-transform`}
      >
        <Icon className="text-lg" />
      </div>
      <div>
        <div className="font-medium text-gray-900">{title}</div>
        <div className="text-sm text-gray-500">{subtitle}</div>
      </div>
    </div>
  </button>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const vendorId = useStore((state) => state.vendorId);
  const userData = useStore((state) => state.userData);
  const setUserData = useStore((state) => state.setUserData);
  const transactions = useStore((state) => state.transactions);
  const setTransactions = useStore((state) => state.setTransactions);
  const transactionsLoaded = useStore((state) => state.transactionsLoaded);
  const locationsCount = useStore((state) => state.locationsCount) || 0;
  const setLocationsCount = useStore((state) => state.setLocationsCount);

  const [loading, setLoading] = useState(!userData);
  const [transactionsLoading, setTransactionsLoading] = useState(!transactionsLoaded);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterPeriod, setFilterPeriod] = useState("today");

  useEffect(() => {
    listenToLocationsCount(setLocationsCount);
  }, []);

  useEffect(() => {
    if (!vendorId) {
      setTransactionsLoading(false);
      return;
    }

    const unsubscribe = getTransactions((data) => {
      // Sort transactions by date (newest first)
      const sortedTransactions = data.sort((a, b) => {
        const dateA =
          a.createdAt && typeof a.createdAt.toDate === "function"
            ? a.createdAt.toDate()
            : a.createdAt && a.createdAt.seconds
            ? new Date(
                a.createdAt.seconds * 1000 +
                  (a.createdAt.nanoseconds || 0) / 1000000
              )
            : new Date(a.createdAt);

        const dateB =
          b.createdAt && typeof b.createdAt.toDate === "function"
            ? b.createdAt.toDate()
            : b.createdAt && b.createdAt.seconds
            ? new Date(
                b.createdAt.seconds * 1000 +
                  (b.createdAt.nanoseconds || 0) / 1000000
              )
            : new Date(b.createdAt);

        return dateB - dateA; // Descending order (newest first)
      });

      setTransactions(sortedTransactions);
      setTransactionsLoading(false);
    });

    // Cleanup subscription on component unmount
    return () => {
      if (unsubscribe && typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [vendorId]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (!userData) {
          setLoading(true);
        }
        const user = await UserData();
        if (!user) {
          throw new Error("No vendor data found");
        }
        setUserData(user);
      } catch (error) {
        console.error("Error fetching user data:", error);
        useStore.getState().clearSessionContext();
        navigate("/login", { replace: true });
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, [navigate]);

  // Function to get today's transactions
  const getTodayTransactions = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return transactions
      .filter((transaction) => {
        let transactionDate;

        if (
          transaction.createdAt &&
          typeof transaction.createdAt.toDate === "function"
        ) {
          transactionDate = transaction.createdAt.toDate();
        } else if (transaction.createdAt && transaction.createdAt.seconds) {
          transactionDate = new Date(
            transaction.createdAt.seconds * 1000 +
              (transaction.createdAt.nanoseconds || 0) / 1000000
          );
        } else {
          transactionDate = new Date(transaction.createdAt);
        }

        return transactionDate >= today && transactionDate < tomorrow;
      });
  };

  const todayTransactions = getTodayTransactions();
  const recentTransactions = transactions.slice(0, 3); // Get the absolute latest 3 transactions

  // Function to calculate today's revenue
  const getTodayRevenue = () => {
    return todayTransactions.reduce(
      (sum, transaction) => sum + (Number(transaction.totalAmount) || 0),
      0
    );
  };

  // Function to calculate this month's revenue
  const getThisMonthRevenue = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(
      now.getFullYear(),
      now.getMonth() + 1,
      0,
      23,
      59,
      59,
      999
    );

    return transactions.reduce((sum, transaction) => {
      let transactionDate;

      if (
        transaction.createdAt &&
        typeof transaction.createdAt.toDate === "function"
      ) {
        transactionDate = transaction.createdAt.toDate();
      } else if (transaction.createdAt && transaction.createdAt.seconds) {
        transactionDate = new Date(
          transaction.createdAt.seconds * 1000 +
            (transaction.createdAt.nanoseconds || 0) / 1000000
        );
      } else {
        transactionDate = new Date(transaction.createdAt);
      }

      if (transactionDate >= startOfMonth && transactionDate <= endOfMonth) {
        return sum + (Number(transaction.totalAmount) || 0);
      }
      return sum;
    }, 0);
  };

  const todayRevenue = getTodayRevenue();
  const thisMonthRevenue = getThisMonthRevenue();

  // Function to calculate total individual services performed today
  const getTotalServicesToday = () => {
    let totalServices = 0;

    todayTransactions.forEach((transaction) => {
      if (transaction.services && transaction.services.length > 0) {
        transaction.services.forEach((service) => {
          const quantity = service.selectedQuantity || 1;
          totalServices += quantity;
        });
      }
    });

    return totalServices;
  };

  const totalServicesToday = getTotalServicesToday();

  // Function to get today's oil change data for the chart
  const getTodayOilChangeData = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const todayOilChanges = transactions.filter((transaction) => {
      let transactionDate;

      if (
        transaction.createdAt &&
        typeof transaction.createdAt.toDate === "function"
      ) {
        transactionDate = transaction.createdAt.toDate();
      } else if (transaction.createdAt && transaction.createdAt.seconds) {
        transactionDate = new Date(
          transaction.createdAt.seconds * 1000 +
            (transaction.createdAt.nanoseconds || 0) / 1000000
        );
      } else {
        transactionDate = new Date(transaction.createdAt);
      }

      // Filter for today's transactions and oil change services
      const isToday = transactionDate >= today && transactionDate < tomorrow;
      const isOilChange =
        transaction.services &&
        transaction.services.some(
          (service) =>
            service.serviceName.toLowerCase().includes("oil") ||
            service.serviceName.toLowerCase().includes("change")
        );

      return isToday && isOilChange;
    });

    // Count different types of oil changes
    const oilTypes = {
      "Conventional Oil": 0,
      "Synthetic Oil": 0,
      "Full Synthetic": 0,
      Other: 0,
    };

    todayOilChanges.forEach((transaction) => {
      transaction.services.forEach((service) => {
        const serviceName = service.serviceName.toLowerCase();
        if (serviceName.includes("conventional")) {
          oilTypes["Conventional Oil"] += service.selectedQuantity || 1;
        } else if (serviceName.includes("synthetic")) {
          if (serviceName.includes("full")) {
            oilTypes["Full Synthetic"] += service.selectedQuantity || 1;
          } else {
            oilTypes["Synthetic Oil"] += service.selectedQuantity || 1;
          }
        } else if (
          serviceName.includes("oil") ||
          serviceName.includes("change")
        ) {
          oilTypes["Other"] += service.selectedQuantity || 1;
        }
      });
    });

    return oilTypes;
  };

  const oilChangeData = getTodayOilChangeData();
  const pieChartData = {
    labels: Object.keys(oilChangeData).filter((key) => oilChangeData[key] > 0),
    datasets: [
      {
        data: Object.values(oilChangeData).filter((value) => value > 0),
        backgroundColor: [
          "rgba(59, 130, 246, 0.8)",
          "rgba(16, 185, 129, 0.8)",
          "rgba(245, 158, 11, 0.8)",
          "rgba(239, 68, 68, 0.8)",
        ],
        borderColor: ["#3B82F6", "#10B981", "#F59E0B", "#EF4444"],
        borderWidth: 2,
        hoverOffset: 8,
      },
    ],
  };

  // Function to get today's non-oil-change services for the chart
  const getTodayOtherServicesData = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const todayOtherServices = transactions.filter((transaction) => {
      let transactionDate;

      if (
        transaction.createdAt &&
        typeof transaction.createdAt.toDate === "function"
      ) {
        transactionDate = transaction.createdAt.toDate();
      } else if (transaction.createdAt && transaction.createdAt.seconds) {
        transactionDate = new Date(
          transaction.createdAt.seconds * 1000 +
            (transaction.createdAt.nanoseconds || 0) / 1000000
        );
      } else {
        transactionDate = new Date(transaction.createdAt);
      }

      // Filter for today's transactions and exclude oil change services
      const isToday = transactionDate >= today && transactionDate < tomorrow;
      const isNotOilChange =
        !transaction.services ||
        !transaction.services.some(
          (service) =>
            service.serviceName.toLowerCase().includes("oil") ||
            service.serviceName.toLowerCase().includes("change")
        );

      return isToday && isNotOilChange;
    });

    // Count different types of services
    const serviceTypes = {};

    todayOtherServices.forEach((transaction) => {
      transaction.services.forEach((service) => {
        const serviceName = service.serviceName.toLowerCase();
        const quantity = service.selectedQuantity || 1;

        // Categorize services
        if (serviceName.includes("wiper") || serviceName.includes("wipe")) {
          serviceTypes["Wipers"] = (serviceTypes["Wipers"] || 0) + quantity;
        } else if (serviceName.includes("filter")) {
          serviceTypes["Filters"] = (serviceTypes["Filters"] || 0) + quantity;
        } else if (serviceName.includes("brake")) {
          serviceTypes["Brake Services"] =
            (serviceTypes["Brake Services"] || 0) + quantity;
        } else if (
          serviceName.includes("tire") ||
          serviceName.includes("tyre")
        ) {
          serviceTypes["Tire Services"] =
            (serviceTypes["Tire Services"] || 0) + quantity;
        } else if (serviceName.includes("battery")) {
          serviceTypes["Battery Services"] =
            (serviceTypes["Battery Services"] || 0) + quantity;
        } else if (
          serviceName.includes("inspection") ||
          serviceName.includes("check")
        ) {
          serviceTypes["Inspections"] =
            (serviceTypes["Inspections"] || 0) + quantity;
        } else {
          // For any other services, use the actual service name (capitalized)
          const displayName =
            service.serviceName.charAt(0).toUpperCase() +
            service.serviceName.slice(1);
          serviceTypes[displayName] =
            (serviceTypes[displayName] || 0) + quantity;
        }
      });
    });

    return serviceTypes;
  };

  const otherServicesData = getTodayOtherServicesData();

  // Sort services by quantity and get top 4, then group the rest as "Other"
  const sortedServices = Object.entries(otherServicesData)
    .sort(([, a], [, b]) => b - a) // Sort by quantity descending
    .slice(0, 4); // Get top 4

  // Calculate "Other" total from remaining services
  const top4Services = sortedServices.reduce((acc, [name, quantity]) => {
    acc[name] = quantity;
    return acc;
  }, {});

  const otherTotal = Object.entries(otherServicesData)
    .filter(([name]) => !top4Services.hasOwnProperty(name))
    .reduce((sum, [, quantity]) => sum + quantity, 0);

  // Create final chart data with top 4 + Other (if Other > 0)
  const finalChartData = { ...top4Services };
  if (otherTotal > 0) {
    finalChartData["Other"] = otherTotal;
  }

  const barChartData = {
    labels: Object.keys(finalChartData),
    datasets: [
      {
        label: "Services Performed Today",
        data: Object.values(finalChartData),
        backgroundColor: [
          "rgba(59, 130, 246, 0.8)",
          "rgba(16, 185, 129, 0.8)",
          "rgba(245, 158, 11, 0.8)",
          "rgba(239, 68, 68, 0.8)",
          "rgba(139, 69, 19, 0.8)",
        ],
        borderColor: ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B4513"],
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
      },
    ],
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          color: "#374151",
          font: { size: 12, weight: "500" },
          padding: 20,
          usePointStyle: true,
        },
      },
      tooltip: {
        backgroundColor: "#1F2937",
        titleColor: "#ffffff",
        bodyColor: "#ffffff",
        cornerRadius: 8,
        displayColors: false,
      },
    },
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#1F2937",
        titleColor: "#ffffff",
        bodyColor: "#ffffff",
        cornerRadius: 8,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          color: "#6B7280",
          font: { size: 11 },
        },
        grid: {
          color: "#F3F4F6",
          drawBorder: false,
        },
      },
      x: {
        ticks: {
          color: "#6B7280",
          font: { size: 11 },
        },
        grid: { display: false },
      },
    },
  };

  const quickActions = [
    {
      icon: FaMapMarkerAlt,
      title: "Branches & Staff",
      subtitle: "Manage branches and their staff members",
      color: "blue",
      onClick: () => navigate("/branches"),
    },
    {
      icon: FaBell,
      title: "Notifications",
      subtitle: "Send notifications to users",
      color: "purple",
      onClick: () => navigate("/notifications"),
    },
    {
      icon: FaChartLine,
      title: "Reports",
      subtitle: "View detailed reports",
      color: "orange",
      onClick: () => navigate("/reports"),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <OnboardingTour />
      <Sidebar />

      <main className="ml-80 p-8 lg:p-10 min-h-screen">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Header Section */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-2">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
                Welcome back, {userData ? userData.firstName : "User"}! 👋
              </h1>
              <p className="text-slate-500 text-sm mt-1">
                Here's what's happening with your business today
              </p>
            </div>
            <div className="flex items-center">
              <div className="bg-white rounded-xl px-4 py-2.5 shadow-2xs border border-slate-200/80 flex items-center space-x-2.5 text-xs font-semibold text-slate-700">
                <FaCalendarAlt className="text-[#007AFF] w-3.5 h-3.5" />
                <span>
                  {new Date().toLocaleDateString("en-US", {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div id="tour-dashboard-stats">
            <DashboardStats
              locationsCount={locationsCount}
              thisMonthRevenue={thisMonthRevenue}
              totalServicesToday={totalServicesToday}
              todayRevenue={todayRevenue}
              loading={loading}
              transactionsLoading={transactionsLoading}
            />
          </div>

          {/* Charts Section */}
          <div id="tour-dashboard-charts">
            <DashboardCharts
              pieChartData={pieChartData}
              barChartData={barChartData}
              pieChartOptions={pieChartOptions}
              barChartOptions={barChartOptions}
              transactionsLoading={transactionsLoading}
            />
          </div>

          {/* Enhanced Transactions */}
          <div id="tour-dashboard-transactions">
            <DashboardTransactions
              recentTransactions={recentTransactions}
              transactionsLoading={transactionsLoading}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
